import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { storageKey } from '../services/utils.service';
import { clearAuthStorage, getDeviceId } from '../services/auth-storage';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';

/** True only for calls to our own API.
 *
 *  Everything else - a third-party service, a static asset - must pass through
 *  untouched: it must not be sent our session token, and a 401 from it must not
 *  be read as "the session expired". Getting this wrong logs the user out when
 *  an unrelated service rejects a request, which is what a third-party postcode
 *  lookup used to do on the add-supplier form. */
function isAppApiRequest(url: string): boolean {
  try {
    const base = document.baseURI;
    return new URL(url, base).href.startsWith(new URL(environment.apiUrl, base).href);
  } catch {
    return false;
  }
}

export const jwtInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const refreshState = inject(TokenRefreshService);

  if (!isAppApiRequest(req.url)) {
    return next(req);
  }

  // Lets the session list tell one browser from another.
  const deviceId = getDeviceId();
  if (deviceId) {
    req = req.clone({ setHeaders: { 'X-Device-Id': deviceId } });
  }

  if (req.url.includes('auth/refresh-token')) {
    return next(req);
  }

  const token = localStorage.getItem(storageKey.accessToken);
  const authReq = token ? addToken(req, token) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !req.url.includes('auth/login')) {
        return handle401Error(authReq, next, authService, router, refreshState);
      }
      return throwError(() => error);
    }),
  );
};

function addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

function handle401Error(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  refreshState: TokenRefreshService,
) {
  if (!refreshState.isRefreshing) {
    refreshState.start();

    return authService.refreshToken().pipe(
      switchMap((tokens: any) => {
        const newToken = tokens.access_token || tokens.accessToken;
        const newRefresh = tokens.refresh_token || tokens.refreshToken;
        localStorage.setItem(storageKey.accessToken, newToken);
        if (newRefresh) {
          localStorage.setItem(storageKey.refreshToken, newRefresh);
        }
        refreshState.succeed(newToken);
        return next(addToken(req, newToken));
      }),
      catchError((err) => {
        refreshState.fail(err);
        clearAuthStorage();
        router.navigate(['/auth/login']);
        return throwError(() => err);
      }),
      // The refresh can also end without either branch running, when the caller
      // unsubscribes - a component that navigates away, or a second click that
      // discards the first request. Queued requests must be released even then.
      finalize(() => refreshState.fail(new Error('Token refresh did not complete'))),
    );
  }

  // Wait for the refresh already in flight rather than starting a second one.
  return refreshState.outcome.pipe(
    filter((outcome) => outcome.status !== 'pending'),
    take(1),
    switchMap((outcome) =>
      outcome.status === 'success'
        ? next(addToken(req, outcome.token))
        : throwError(() => outcome.error),
    ),
  );
}
